import { arktypeResolver } from "@hookform/resolvers/arktype";
import { type } from "arktype";
import { Loader2 } from "lucide-react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import type { CustomFieldInputType } from "@/api/db/connection";
import { CustomFieldValues } from "@/api/document/custom-field-values";
import type { EditorField } from "@/components/document-editor/fields-context";
import { FormField } from "@/components/ui/form-field";
import { OverlayShell } from "@/components/ui/overlay";
import { useCustomFieldCreate, useCustomFieldUpdate } from "@/hooks/use-terms";
import { errorCode } from "@/lib/error-code";
import { DocumentFieldLabels } from "@/lib/labels/document-fields";
import { TermosLabels } from "@/lib/labels/termos";
import { kebab } from "@/lib/slug";
import { cn } from "@/lib/utils";

type FieldDraft =
	| { mode: "create"; name: string; label: string; patternId: number | null }
	| { mode: "edit"; field: EditorField & { inputType: CustomFieldInputType } };

const inputType = type.enumerated(...CustomFieldValues.inputTypes);
const createForm = type({
	mode: type.enumerated("create"),
	label: type("string.trim").to("string > 0"),
	name: type("string.trim").pipe(kebab).to("string > 0"),
	inputType,
});
const editForm = type({
	mode: type.enumerated("edit"),
	label: type("string.trim").to("string > 0"),
	name: "string",
	inputType,
});
const fieldForm = createForm.or(editForm);

export function FieldDialog({
	termId,
	draft,
	onClose,
	onSaved,
}: {
	termId: number;
	draft: FieldDraft;
	onClose(): void;
	onSaved(field: EditorField): void;
}) {
	const create = useCustomFieldCreate();
	const update = useCustomFieldUpdate();
	const pending = create.isPending || update.isPending;
	const {
		control,
		handleSubmit,
		setValue,
		getFieldState,
		formState: { isValid },
	} = useForm<typeof fieldForm.inferIn, unknown, typeof fieldForm.inferOut>({
		resolver: arktypeResolver(fieldForm),
		mode: "onChange",
		defaultValues:
			draft.mode === "create"
				? { mode: "create", label: draft.label, name: draft.name, inputType: "text" }
				: { mode: "edit", label: draft.field.label, name: "", inputType: draft.field.inputType },
	});
	const selectedInputType = useWatch({ control, name: "inputType" });
	const title =
		draft.mode === "create"
			? TermosLabels.customField.createTitle
			: TermosLabels.customField.editTitle;
	const submitLabel =
		draft.mode === "create" ? TermosLabels.customField.create : TermosLabels.customField.save;

	const changeLabel = (value: string) => {
		setValue("label", value, { shouldDirty: true, shouldValidate: true });

		if (draft.mode === "create" && !getFieldState("name").isDirty) {
			setValue("name", kebab(value), { shouldValidate: true });
		}
	};

	const submit = handleSubmit((values) => {
		if (values.mode === "edit" && draft.mode === "edit") {
			update.mutate(
				{
					termId,
					fieldId: draft.field.id,
					label: values.label,
					inputType: values.inputType,
				},
				{
					onSuccess: () => {
						onSaved({ ...draft.field, label: values.label, inputType: values.inputType });
						onClose();
					},
					onError: (error) => toast.error(TermosLabels.customField.error(errorCode(error))),
				},
			);

			return;
		}

		if (values.mode !== "create" || draft.mode !== "create") {
			return;
		}

		create.mutate(
			{
				termId,
				name: values.name,
				label: values.label,
				inputType: values.inputType,
				patternId: draft.patternId,
				source: "input",
			},
			{
				onSuccess: ({ id }) => {
					onSaved({
						id,
						label: values.label,
						value: null,
						inputType: values.inputType,
						patternId: draft.patternId,
						source: "input",
					});
					onClose();
				},
				onError: (error) => toast.error(TermosLabels.customField.error(errorCode(error))),
			},
		);
	});

	return (
		<OverlayShell label={title} onClose={onClose} className="items-center justify-center p-4">
			<form
				data-component="term-field-dialog"
				data-mode={draft.mode}
				onSubmit={submit}
				className="overlay-panel max-w-lg gap-4 p-5"
			>
				<div>
					<h2 className="type-section-title text-foreground">{title}</h2>
					<p className="mt-0.5 text-xs text-muted-foreground">
						{TermosLabels.customField.createHint}
					</p>
				</div>

				<Controller
					control={control}
					name="label"
					render={({ field }) => (
						<FormField
							label={TermosLabels.customField.labelLabel}
							value={field.value}
							onChange={changeLabel}
							placeholder={TermosLabels.customField.labelPlaceholder}
							autoFocus
							slot="custom-field-label"
						/>
					)}
				/>

				{draft.mode === "create" && (
					<Controller
						control={control}
						name="name"
						render={({ field }) => (
							<FormField
								label={TermosLabels.customField.nameLabel}
								value={field.value}
								onChange={(value) =>
									setValue("name", value, { shouldDirty: true, shouldValidate: true })
								}
								hint={TermosLabels.customField.nameHint}
								slot="custom-field-name"
							/>
						)}
					/>
				)}

				<fieldset className="flex flex-col gap-2">
					<legend className="text-xs font-medium text-foreground">
						{TermosLabels.customField.typeLabel}
					</legend>

					<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
						{CustomFieldValues.inputTypes.map((candidate) => (
							<button
								key={candidate}
								type="button"
								data-slot="field-type"
								data-input-type={candidate}
								aria-pressed={selectedInputType === candidate}
								onClick={() =>
									setValue("inputType", candidate, {
										shouldDirty: true,
										shouldValidate: true,
									})
								}
								className={cn(
									"rounded-md border px-2.5 py-2 text-left text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
									selectedInputType === candidate && "border-primary bg-primary/10 text-primary",
									selectedInputType !== candidate &&
										"border-border text-muted-foreground hover:bg-background hover:text-foreground",
								)}
							>
								{DocumentFieldLabels.inputTypes[candidate].label}
							</button>
						))}
					</div>
				</fieldset>

				<div className="mt-1 flex items-center justify-end gap-2">
					<Button type="button" variant="secondary" size="sm" onClick={onClose}>
						Cancelar
					</Button>

					<Button
						variant="secondary"
						size="sm"
						type="submit"
						data-slot="custom-field-submit"
						disabled={!isValid || pending}
					>
						{pending && <Loader2 className="size-4 animate-spin" />}
						{submitLabel}
					</Button>
				</div>
			</form>
		</OverlayShell>
	);
}
